import { Sphere, BufferAttribute } from 'three';
const preparedFields=new WeakMap();
// Refresh once after each cage step, then share this field across its surfaces.
export function prepareSurface(body){
  let field=preparedFields.get(body);
  if(!field){field={displacement:new Float64Array(body.p.length),min:body.min,anchorBottom:body.anchorBottom};preparedFields.set(body,field);}
  field.floor=body.floor??null;
  for(let i=0;i<body.p.length;i++)field.displacement[i]=body.p[i]-body.initialRest[i];
  return field;
}
// Transport authored normals through the smooth cage field. Triangle normals
// introduce shading facets when a coarse or split-index surface bends.
export function bindSurface(body,point,normal){
  const binding=body.bind(point),axes=[];
  for(let a=0;a<3;a++){
    const scale=(body.n[a]-1)/(body.max[a]-body.min[a]);
    const t=(point[a]-body.min[a])*scale,mid=Math.floor(t+.5),axis=[];
    for(let j=mid-1;j<=mid+1;j++){
      const d=t-j,abs=Math.abs(d);
      axis.push([abs<.5?.75-d*d:.5*(1.5-abs)**2,(abs<.5?-2*d:-Math.sign(d)*(1.5-abs))*scale]);
    }
    axes.push(axis);
  }
  const [nx,ny,nz]=normal;
  let tx,ty,tz;
  if(Math.abs(nx)<.9){tx=1-nx*nx;ty=-nx*ny;tz=-nx*nz;}
  else {tx=-ny*nx;ty=1-ny*ny;tz=-ny*nz;}
  const length=Math.hypot(tx,ty,tz);tx/=length;ty/=length;tz/=length;
  const ux=ny*tz-nz*ty,uy=nz*tx-nx*tz,uz=nx*ty-ny*tx;
  const gradients=new Float32Array(54);let k=0;
  for(const [wz,dz]of axes[2])for(const [wy,dy]of axes[1])for(const [wx,dx]of axes[0]){
    const gx=dx*wy*wz,gy=wx*dy*wz,gz=wx*wy*dz;
    gradients[k++]=gx*tx+gy*ty+gz*tz;gradients[k++]=gx*ux+gy*uy+gz*uz;
  }
  return {...binding,indices:Uint16Array.from(binding.indices),weights:Float64Array.from(binding.weights),gradients,normal:[...normal],tangents:[tx,ty,tz,ux,uy,uz]};
}
export function sampleSurface(body,binding,out,normal,amount=1){
  let [tx,ty,tz,ux,uy,uz]=binding.tangents;
  let px=binding.point[0],py=binding.point[1],pz=binding.point[2];
  const p=body.p,r=body.initialRest,d=body.displacement,grad=binding.gradients,indices=binding.indices,weights=binding.weights;
  for(let k=0;k<indices.length;k++){
    const i=indices[k]*3,w=weights[k];
    const x=(d?d[i]:p[i]-r[i])*amount,y=(d?d[i+1]:p[i+1]-r[i+1])*amount,z=(d?d[i+2]:p[i+2]-r[i+2])*amount;
    const gt=grad[k*2],gu=grad[k*2+1];
    px+=x*w;py+=y*w;pz+=z*w;
    tx+=x*gt;ty+=y*gt;tz+=z*gt;ux+=x*gu;uy+=y*gu;uz+=z*gu;
  }
  out[0]=px;out[1]=body.anchorBottom!==false?Math.max(body.min[1],py):body.floor!=null?Math.max(body.floor,py):py;out[2]=pz;
  // Cross(J*t, J*u) equals cofactor(J)*normal. Two directional derivatives
  // preserve the same normal with less work than a full 3x3 Jacobian.
  const [nx,ny,nz]=binding.normal;
  const x=ty*uz-tz*uy,y=tz*ux-tx*uz,z=tx*uy-ty*ux;
  const length=Math.sqrt(x*x+y*y+z*z);
  if(length>1e-10){normal[0]=x/length;normal[1]=y/length;normal[2]=z/length;}
  else {normal[0]=nx;normal[1]=ny;normal[2]=nz;}
  return out;
}

export function updateSurfaceBounds(geometry){
  geometry.computeBoundingBox();
  geometry.boundingSphere??=new Sphere();
  geometry.boundingBox.getBoundingSphere(geometry.boundingSphere);
}

// Split every triangle into four at its edge midpoints, sharing each new
// vertex between the two triangles on its edge. Flat at rest, but a coarse
// skin then bends smoothly enough under a deep dent that the face painted on
// it is not hidden behind its straight triangles. Indexed geometry only.
export function splitTriangles(geometry){
  const index=geometry.index;if(!index)return geometry;
  const n=geometry.attributes.position.count,edges=new Map(),pairs=[],out=[];
  const mid=(a,b)=>{const key=a<b?a*n+b:b*n+a;let m=edges.get(key);if(m===undefined){m=n+pairs.length/2;pairs.push(a,b);edges.set(key,m);}return m;};
  for(let i=0;i<index.count;i+=3){
    const a=index.getX(i),b=index.getX(i+1),c=index.getX(i+2),ab=mid(a,b),bc=mid(b,c),ca=mid(c,a);
    out.push(a,ab,ca,ab,b,bc,ca,bc,c,ab,bc,ca);
  }
  const count=n+pairs.length/2;
  for(const [name,attribute] of Object.entries(geometry.attributes)){
    // Normalised integers are read as floats, so store floats.
    const size=attribute.itemSize,array=attribute.normalized?new Float32Array(count*size):new attribute.array.constructor(count*size);
    for(let i=0;i<n;i++)for(let k=0;k<size;k++)array[i*size+k]=attribute.getComponent(i,k);
    for(let j=0;j<pairs.length;j+=2){
      const v=(n+j/2)*size,a=pairs[j],b=pairs[j+1];
      for(let k=0;k<size;k++)array[v+k]=(attribute.getComponent(a,k)+attribute.getComponent(b,k))/2;
      if(name==='normal'){const l=Math.hypot(array[v],array[v+1],array[v+2])||1;for(let k=0;k<3;k++)array[v+k]/=l;}
    }
    geometry.setAttribute(name,new BufferAttribute(array,size,false));
  }
  geometry.setIndex(out);
  for(const group of geometry.groups){group.start*=4;group.count*=4;}
  return geometry;
}
